import { createContext, createElement, useContext, useRef } from "react";
import { useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";

import { STORAGE_KEYS } from "~/lib/constants";
import { POST_SIGNUP_SMS_CONTEXT_TTL_MS, type PromotionalSmsOutcome } from "~/lib/post-signup-sms-opt-in";

export const POST_SIGNUP_SMS_OPT_IN_FLAG_KEY = "post-signup-sms-opt-in-popup" as const;

const PERSISTED_PENDING_VERSION = 1;

export type PostSignupSmsOptInFailureCategory = "request" | "confirmation" | "reconciliation";

export type PostSignupSmsOptInPhase = "idle" | "pending" | "visible" | "saving" | "failed" | "reconciling";

export type PostSignupSmsOptInCandidate = {
  candidateId: string;
  userExternalId: string;
  createdAt: number;
  expiresAt: number;
  flagKey: typeof POST_SIGNUP_SMS_OPT_IN_FLAG_KEY;
  flagEnabled: true;
};

export type PostSignupSmsOptInEligibilityInput = {
  submissionSucceeded: boolean;
  promotionalSmsOutcome: PromotionalSmsOutcome;
  followUpFlagEnabled: boolean | null | undefined;
  userExternalId: string | null | undefined;
};

export type AccountCreationCompletedOptions = PostSignupSmsOptInEligibilityInput & {
  now?: number;
};

export type PostSignupSmsOptInDismissalDisposition = "dismissed" | "reconciling" | "ignored";

type PostSignupSmsOptInStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type PersistedPendingCandidate = {
  version: typeof PERSISTED_PENDING_VERSION;
  candidate: PostSignupSmsOptInCandidate;
};

export type PostSignupSmsOptInActions = {
  accountCreationCompleted: (options: AccountCreationCompletedOptions) => boolean;
  pendingCandidateInvalidated: (candidateId?: string) => boolean;
  popupBecameVisible: (candidateId: string, now?: number) => boolean;
  optInRequested: (candidateId: string) => boolean;
  optInFailed: (
    candidateId: string,
    category: PostSignupSmsOptInFailureCategory,
    options?: { reconciliationRequired?: boolean },
  ) => boolean;
  optInSucceeded: (candidateId: string) => boolean;
  dismissalRequested: (candidateId: string) => PostSignupSmsOptInDismissalDisposition;
  reconciliationSucceeded: (candidateId: string) => boolean;
  reconciliationFailed: (candidateId: string) => boolean;
  clearSession: () => void;
};

export type PostSignupSmsOptInState = {
  phase: PostSignupSmsOptInPhase;
  candidate: PostSignupSmsOptInCandidate | null;
  failureCategory: PostSignupSmsOptInFailureCategory | null;
  reconciliationRequired: boolean;
  actions: PostSignupSmsOptInActions;
};

export type PostSignupSmsOptInStoreApi = StoreApi<PostSignupSmsOptInState>;

type CreatePostSignupSmsOptInStoreOptions = {
  storage?: PostSignupSmsOptInStorage | null;
  now?: () => number;
  createCandidateId?: () => string;
};

const idleState: Pick<PostSignupSmsOptInState, "phase" | "candidate" | "failureCategory" | "reconciliationRequired"> = {
  phase: "idle",
  candidate: null,
  failureCategory: null,
  reconciliationRequired: false,
};

function getBrowserSessionStorage(): PostSignupSmsOptInStorage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isCandidate(value: unknown): value is PostSignupSmsOptInCandidate {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<PostSignupSmsOptInCandidate>;
  return (
    typeof candidate.candidateId === "string" &&
    candidate.candidateId.length > 0 &&
    typeof candidate.userExternalId === "string" &&
    candidate.userExternalId.length > 0 &&
    typeof candidate.createdAt === "number" &&
    Number.isFinite(candidate.createdAt) &&
    typeof candidate.expiresAt === "number" &&
    Number.isFinite(candidate.expiresAt) &&
    candidate.expiresAt >= candidate.createdAt &&
    candidate.flagKey === POST_SIGNUP_SMS_OPT_IN_FLAG_KEY &&
    candidate.flagEnabled === true
  );
}

function readPersistedPendingCandidate(storage: PostSignupSmsOptInStorage | null): PostSignupSmsOptInCandidate | null {
  if (!storage) return null;

  try {
    const rawValue = storage.getItem(STORAGE_KEYS.POST_SIGNUP_SMS_OPT_IN);
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as Partial<PersistedPendingCandidate>;
    if (value.version !== PERSISTED_PENDING_VERSION || !isCandidate(value.candidate)) return null;

    return value.candidate;
  } catch {
    return null;
  }
}

function writePersistedPendingCandidate(
  storage: PostSignupSmsOptInStorage | null,
  candidate: PostSignupSmsOptInCandidate,
): boolean {
  if (!storage) return false;

  try {
    const value: PersistedPendingCandidate = {
      version: PERSISTED_PENDING_VERSION,
      candidate,
    };
    storage.setItem(STORAGE_KEYS.POST_SIGNUP_SMS_OPT_IN, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function removePersistedPendingCandidate(
  storage: PostSignupSmsOptInStorage | null,
  expectedCandidateId?: string,
): boolean {
  if (!storage) return false;

  try {
    if (expectedCandidateId) {
      const persistedCandidate = readPersistedPendingCandidate(storage);
      if (persistedCandidate?.candidateId !== expectedCandidateId) return false;
    }

    storage.removeItem(STORAGE_KEYS.POST_SIGNUP_SMS_OPT_IN);
    return true;
  } catch {
    return false;
  }
}

function createCandidateId(): string {
  return globalThis.crypto.randomUUID();
}

function activeCandidateMatches(
  state: PostSignupSmsOptInState,
  candidateId: string,
  allowedPhases: Array<PostSignupSmsOptInPhase>,
): boolean {
  return state.candidate?.candidateId === candidateId && allowedPhases.includes(state.phase);
}

export function isPostSignupSmsOptInEligible(input: PostSignupSmsOptInEligibilityInput): boolean {
  return (
    input.submissionSucceeded &&
    input.promotionalSmsOutcome === "declined" &&
    input.followUpFlagEnabled === true &&
    Boolean(input.userExternalId?.trim())
  );
}

export function hasPostSignupSmsOptInPendingToken(
  candidate: PostSignupSmsOptInCandidate | null,
  storage: PostSignupSmsOptInStorage | null = getBrowserSessionStorage(),
): boolean {
  if (!candidate) return false;

  const persistedCandidate = readPersistedPendingCandidate(storage);
  return Boolean(
    persistedCandidate &&
      persistedCandidate.candidateId === candidate.candidateId &&
      persistedCandidate.userExternalId === candidate.userExternalId &&
      persistedCandidate.createdAt === candidate.createdAt &&
      persistedCandidate.expiresAt === candidate.expiresAt &&
      persistedCandidate.flagKey === candidate.flagKey &&
      persistedCandidate.flagEnabled === candidate.flagEnabled,
  );
}

export function clearPostSignupSmsOptInPendingCandidate(
  storage: PostSignupSmsOptInStorage | null = getBrowserSessionStorage(),
): void {
  removePersistedPendingCandidate(storage);
}

export function createPostSignupSmsOptInStore({
  storage = getBrowserSessionStorage(),
  now = Date.now,
  createCandidateId: createCandidateIdForStore = createCandidateId,
}: CreatePostSignupSmsOptInStoreOptions = {}): PostSignupSmsOptInStoreApi {
  const persistedCandidate = readPersistedPendingCandidate(storage);
  const initialCandidate = persistedCandidate && persistedCandidate.expiresAt >= now() ? persistedCandidate : null;

  if (persistedCandidate && !initialCandidate) removePersistedPendingCandidate(storage, persistedCandidate.candidateId);

  return createStore<PostSignupSmsOptInState>()((set, get) => {
    const reset = (expectedCandidateId?: string) => {
      const activeCandidateId = get().candidate?.candidateId;
      if (expectedCandidateId && activeCandidateId !== expectedCandidateId) return false;

      if (activeCandidateId) removePersistedPendingCandidate(storage, activeCandidateId);
      set(idleState);
      return true;
    };

    return {
      ...idleState,
      ...(initialCandidate ? { phase: "pending" as const, candidate: initialCandidate } : {}),
      actions: {
        accountCreationCompleted: (options) => {
          if (!isPostSignupSmsOptInEligible(options)) {
            removePersistedPendingCandidate(storage);
            set(idleState);
            return false;
          }

          const createdAt = options.now ?? now();
          const candidate: PostSignupSmsOptInCandidate = {
            candidateId: createCandidateIdForStore(),
            userExternalId: options.userExternalId?.trim() ?? "",
            createdAt,
            expiresAt: createdAt + POST_SIGNUP_SMS_CONTEXT_TTL_MS,
            flagKey: POST_SIGNUP_SMS_OPT_IN_FLAG_KEY,
            flagEnabled: true,
          };

          if (!writePersistedPendingCandidate(storage, candidate)) {
            set(idleState);
            return false;
          }

          set({
            phase: "pending",
            candidate,
            failureCategory: null,
            reconciliationRequired: false,
          });
          return true;
        },
        pendingCandidateInvalidated: (candidateId) => reset(candidateId),
        popupBecameVisible: (candidateId, observedAt = now()) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["pending"])) return false;

          const candidate = state.candidate;
          if (!candidate || candidate.expiresAt < observedAt) {
            reset(candidateId);
            return false;
          }

          if (!hasPostSignupSmsOptInPendingToken(candidate, storage)) {
            set(idleState);
            return false;
          }

          removePersistedPendingCandidate(storage, candidateId);
          set({ phase: "visible", failureCategory: null, reconciliationRequired: false });
          return true;
        },
        optInRequested: (candidateId) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["visible", "failed"])) return false;

          set({ phase: "saving", failureCategory: null, reconciliationRequired: false });
          return true;
        },
        optInFailed: (candidateId, category, options) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["saving"])) return false;

          set({
            phase: "failed",
            failureCategory: category,
            reconciliationRequired: options?.reconciliationRequired === true,
          });
          return true;
        },
        optInSucceeded: (candidateId) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["saving"])) return false;
          return reset(candidateId);
        },
        dismissalRequested: (candidateId) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["visible", "failed"])) return "ignored";

          if (state.phase === "failed" && state.reconciliationRequired) {
            set({ phase: "reconciling", failureCategory: null });
            return "reconciling";
          }

          reset(candidateId);
          return "dismissed";
        },
        reconciliationSucceeded: (candidateId) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["reconciling"])) return false;
          return reset(candidateId);
        },
        reconciliationFailed: (candidateId) => {
          const state = get();
          if (!activeCandidateMatches(state, candidateId, ["reconciling"])) return false;

          set({ phase: "failed", failureCategory: "reconciliation", reconciliationRequired: true });
          return true;
        },
        clearSession: () => {
          removePersistedPendingCandidate(storage);
          set(idleState);
        },
      },
    };
  });
}

const PostSignupSmsOptInStoreContext = createContext<PostSignupSmsOptInStoreApi | null>(null);

export function PostSignupSmsOptInStoreProvider({
  children,
  store,
}: {
  children: React.ReactNode;
  store?: PostSignupSmsOptInStoreApi;
}) {
  const storeRef = useRef<PostSignupSmsOptInStoreApi | null>(null);

  if (!storeRef.current) storeRef.current = store ?? createPostSignupSmsOptInStore();

  return createElement(PostSignupSmsOptInStoreContext.Provider, { value: storeRef.current }, children);
}

export function usePostSignupSmsOptInStoreApi(): PostSignupSmsOptInStoreApi {
  const store = useContext(PostSignupSmsOptInStoreContext);
  // biome-ignore lint/security/noSecrets: provider invariant message, not a credential
  if (!store) throw new Error("PostSignupSmsOptInStoreProvider is missing");
  return store;
}

export function usePostSignupSmsOptInStore<T>(selector: (state: PostSignupSmsOptInState) => T): T {
  return useStore(usePostSignupSmsOptInStoreApi(), selector);
}

export function usePostSignupSmsOptInPhase(): PostSignupSmsOptInPhase {
  return usePostSignupSmsOptInStore((state) => state.phase);
}

export function usePostSignupSmsOptInCandidate(): PostSignupSmsOptInCandidate | null {
  return usePostSignupSmsOptInStore((state) => state.candidate);
}

export function usePostSignupSmsOptInFailureCategory(): PostSignupSmsOptInFailureCategory | null {
  return usePostSignupSmsOptInStore((state) => state.failureCategory);
}

export function usePostSignupSmsOptInReconciliationRequired(): boolean {
  return usePostSignupSmsOptInStore((state) => state.reconciliationRequired);
}

export function usePostSignupSmsOptInActions(): PostSignupSmsOptInActions {
  return usePostSignupSmsOptInStore((state) => state.actions);
}
